/**
 * Advisory signals for a freshly-imported bill history.
 *
 * Bulk import creates clean rows without a review step, so the only thing
 * standing between a mis-read amount and the user's cost history is a signal
 * pointing at it. These rules are ADVISORY: they annotate rows that already
 * exist and never block, un-create, or gate anything.
 *
 * They live on the client on purpose. The server sees one file per request, so
 * it cannot compute anything population-dependent — a "batch median" of one row
 * is meaningless. The client holds the whole bill list (pre-existing rows plus
 * everything just created), which is the true population. Row-LOCAL signals
 * (low confidence, implausible date) are computed server-side instead, since
 * they need data the client never sees.
 *
 * Deliberately only two rules to start. A badly-calibrated rule that flags 8 of
 * 36 rows trains the user to ignore the list, which is worse than no list at
 * all; the statistical rules (same-month median, MAD) need real imported data
 * to tune against first. See ADR-0010.
 */

import { findOverlappingPeriods, monthIndexOfDay, toPeriod, toUtcDay } from './billCoverage';

const amountOf = (bill) => {
  const n = typeof bill.amount === 'string' ? parseFloat(bill.amount) : bill.amount;
  return Number.isFinite(n) ? n : null;
};

// UTC calendar month (0-11). Local getMonth() on the API's RFC-1123 dates put a
// bill ending on the 1st in the previous month for viewers west of UTC.
const monthOf = (bill) => {
  const day = toUtcDay(bill.period_end);
  return day == null ? null : monthIndexOfDay(day) % 12;
};

export const median = (nums) => {
  const sorted = nums.filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/**
 * Powers of ten we treat as a misplaced decimal point. $142.50 read as $14250
 * or $14.25 is THE characteristic extraction failure, and unlike a "high" bill
 * it is unambiguous — no real utility charges 100x one month.
 */
const DECIMAL_FACTORS = [100, 10, 0.1, 0.01];
const FACTOR_TOLERANCE = 0.15;
/** Below this, "baseline" is noise and every rule would be guessing. */
const MIN_POPULATION = 3;

/**
 * Compare each bill against the median of its OWN calendar month across years,
 * falling back to the whole population when that month has no peers.
 *
 * Same-month comparison is what makes this seasonality-safe: a January gas bill
 * is only comparable to another January. A global mean would flag every winter
 * month in a heating-fuel history and the list would be useless.
 */
export const findDecimalShifts = (bills = []) => {
  const priced = bills.filter((b) => amountOf(b) != null);
  if (priced.length < MIN_POPULATION) return [];

  const found = [];
  priced.forEach((bill) => {
    const amount = amountOf(bill);
    const month = monthOf(bill);
    const samePeers = priced.filter((b) => b !== bill && month != null && monthOf(b) === month);
    const peers = (samePeers.length ? samePeers : priced.filter((b) => b !== bill)).map(amountOf);
    const baseline = median(peers);
    if (!baseline) return;

    const ratio = amount / baseline;
    const factor = DECIMAL_FACTORS.find((f) => Math.abs(ratio - f) <= f * FACTOR_TOLERANCE);
    if (factor) {
      found.push({
        id: bill.id,
        reason: 'decimal_shift',
        amount,
        baseline,
        factor,
        comparedToSameMonth: samePeers.length > 0,
      });
    }
  });
  return found;
};

/**
 * Bills whose service periods intersect without being identical.
 *
 * uq_utility_bill_utility_period only catches EXACT duplicates, so two bills
 * for the same month whose periods differ by a few days both persist and
 * silently double-count in the trailing-12-month average. Nothing else in the
 * product catches this, which makes it the highest-value rule here.
 *
 * Periods are half-open (billCoverage.js), so bills that merely share a
 * meter-read date (Jun 2 – Jul 2, then Jul 2 – Aug 4) are not reported.
 */
export const findOverlaps = (bills = []) =>
  findOverlappingPeriods(bills.map(toPeriod).filter(Boolean)).map(({ a, b }) => [
    a.bill.id,
    b.bill.id,
  ]);

export const computeImportSignals = (bills = []) => ({
  decimalShifts: findDecimalShifts(bills),
  overlaps: findOverlaps(bills),
});
